// frontend/src/components/trades/TradeReview.tsx
//
// "Why did it win or lose?" for one tracked trade (backend tradeReview.ts):
// the headline cause, each finding with whether it helped or hurt, the
// lesson with its Playbook section, and the numbers behind it.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@heroui/react";
import { CircleMinus, CirclePlus, Circle, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TradeReviewData {
  outcome: "won" | "lost" | "even" | "open";
  headline: string;
  reasons: Array<{ key: string; tone: "for" | "against" | "neutral"; text: string }>;
  lesson: string;
  stats: {
    bestR: number; worstR: number; hoursHeld: number;
    btcChangePct: number | null;
    htfAtEntry: string | null; htfAtExit: string | null;
  };
}

/** A stored review on a trade row (closed trades), or null. */
export function parseReview(raw: unknown): TradeReviewData | null {
  if (typeof raw !== "string" || !raw) return null;
  try { return JSON.parse(raw) as TradeReviewData; } catch { return null; }
}

export function useTradeReview(id: string | null) {
  const [review, setReview] = useState<TradeReviewData | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setReview(null);
    setError(false);
    fetch(`/api/tracked-trades/${encodeURIComponent(id)}/review`)
      .then(r => r.json())
      .then(d => { if (!cancelled) d.success ? setReview(d.review) : setError(true); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [id]);
  return { review, error };
}

const OUTCOME_TONE = { won: "text-profit", lost: "text-loss", even: "text-foreground", open: "text-foreground" } as const;
const TONE_ICON = {
  for: { Icon: CirclePlus, cls: "text-profit", label: "helped" },
  against: { Icon: CircleMinus, cls: "text-loss", label: "hurt" },
  neutral: { Icon: Circle, cls: "text-muted", label: "note" },
} as const;

/** Playbook › N. Section → a link to that section. */
const PLAYBOOK_ANCHOR: Record<string, string> = {
  "1": "funnel", "2": "setups", "3": "patterns", "4": "managing", "5": "risk",
};

export function TradeReviewCard({ review, className }: { review: TradeReviewData; className?: string }) {
  const s = review.stats;
  const section = review.lesson.match(/Playbook › (\d)/)?.[1];
  return (
    <div className={cn("space-y-3 text-sm", className)}>
      <p className={cn("text-base font-semibold leading-snug", OUTCOME_TONE[review.outcome])}>{review.headline}</p>

      <ul className="space-y-2">
        {review.reasons.map(r => {
          const t = TONE_ICON[r.tone];
          return (
            <li key={r.key} className="flex gap-2 leading-relaxed">
              <t.Icon aria-label={t.label} className={cn("mt-0.5 size-4 shrink-0", t.cls)} />
              <span>{r.text}</span>
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2 rounded-lg border border-border bg-surface-secondary px-3 py-2.5 leading-relaxed">
        <BookOpen className="mt-0.5 size-4 shrink-0 text-accent-soft-foreground" aria-hidden />
        <p>
          <span className="font-semibold">Lesson: </span>
          {review.lesson}
          {section && PLAYBOOK_ANCHOR[section] && (
            <> <Link to={`/playbook#${PLAYBOOK_ANCHOR[section]}`} className="text-brand-text underline underline-offset-2">Open it</Link></>
          )}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted sm:grid-cols-4">
        <div><dt>Best point</dt><dd className="num text-foreground">+{s.bestR.toFixed(1)}R</dd></div>
        <div><dt>Worst point</dt><dd className="num text-foreground">−{s.worstR.toFixed(1)}R</dd></div>
        <div><dt>Time in trade</dt><dd className="num text-foreground">{s.hoursHeld < 1 ? `${Math.max(1, Math.round(s.hoursHeld * 60))} min` : `${s.hoursHeld.toFixed(1)}h`}</dd></div>
        <div>
          <dt>BTC meanwhile</dt>
          <dd className="num text-foreground">
            {s.btcChangePct == null ? "—" : Math.abs(s.btcChangePct) < 0.05 ? "flat" : `${s.btcChangePct > 0 ? "+" : "−"}${Math.abs(s.btcChangePct).toFixed(1)}%`}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/** Loads and shows the review for one trade. */
export function TradeReviewLoader({ id }: { id: string }) {
  const { review, error } = useTradeReview(id);
  if (error) return <p className="text-sm text-muted">Couldn't load the review. Try again in a minute.</p>;
  if (!review) {
    return (
      <div className="space-y-2" aria-busy="true">
        <Skeleton className="h-5 w-2/3 rounded" />
        <Skeleton className="h-4 w-full rounded" />
        <Skeleton className="h-4 w-5/6 rounded" />
      </div>
    );
  }
  return <TradeReviewCard review={review} />;
}
